import React, { useState, useEffect, useCallback } from "react";
import {
  SimpleButton,
  LoadingButton,
  TextArea,
  Select,
  Input,
  NotificationsList,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsPlus, BsCheckCircle, BsCheckLg, BsXCircle, BsClipboardCheck } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";
import PppDialog from "./PppDialog";

const ESITO_CFG = {
  completo:              { cls: "bg-green-100 text-green-800",  label: "Completo",               icon: "✓" },
  parzialmente_completo: { cls: "bg-yellow-100 text-yellow-800", label: "Parzialmente completo", icon: "~" },
  inadeguato:            { cls: "bg-red-100 text-red-800",      label: "Inadeguato",              icon: "✗" },
};

const ESITO_VOCE_CFG = {
  conforme:      { cls: "bg-green-100 text-green-700",  label: "Conforme" },
  non_conforme:  { cls: "bg-red-100 text-red-700",      label: "Non conforme" },
  parziale:      { cls: "bg-yellow-100 text-yellow-700", label: "Parziale" },
  non_verificato:{ cls: "bg-gray-100 text-gray-600",    label: "Non verificato" },
};

const ESITO_VOCE_OPTIONS = [
  { value: "conforme",       label: "Conforme" },
  { value: "non_conforme",   label: "Non conforme" },
  { value: "parziale",       label: "Parziale" },
  { value: "non_verificato", label: "Non verificato" },
];

const ESITO_VALUTAZIONE_OPTIONS = [
  { value: "completo",               label: "Completo — integrazione adeguata" },
  { value: "parzialmente_completo",  label: "Parzialmente completo" },
  { value: "inadeguato",             label: "Inadeguato — provvedimento di respingimento" },
];

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function EsitoBadge({ esito }) {
  const c = ESITO_CFG[esito];
  if (!c) return null;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${c.cls}`}>
      {c.icon} {c.label}
    </span>
  );
}

function VoceChecklistRow({ voce, onEdit, readonly }) {
  const cfg = ESITO_VOCE_CFG[voce.esito] ?? { cls: "", label: voce.esito };
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
      <span className={`mt-0.5 px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${cfg.cls}`}>
        {cfg.label}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800">{voce.etichetta}</p>
        {voce.note && <p className="text-xs text-gray-500 mt-0.5">{voce.note}</p>}
      </div>
      {!readonly && (
        <button
          onClick={() => onEdit(voce)}
          className="text-xs text-indigo-600 hover:underline shrink-0"
        >
          Modifica
        </button>
      )}
    </div>
  );
}

function ValutazioneCard({ valutazione, onAggiornaChecklist, onCompleta, canGestisci }) {
  const readonly = valutazione.completata || !canGestisci;

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-5 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {valutazione.esito ? (
              <EsitoBadge esito={valutazione.esito} />
            ) : (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded">
                In corso
              </span>
            )}
            {valutazione.fase12_avviata && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                Fase 12 avviata
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-1">
            {valutazione.richiesta_descrizione
              ? valutazione.richiesta_descrizione
              : Array.isArray(valutazione.richiesta_tipi_integrazione) && valutazione.richiesta_tipi_integrazione.length > 0
                ? valutazione.richiesta_tipi_integrazione.join(", ")
                : `Richiesta #${valutazione.richiesta_integrazione_id}`}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Creata il {formatDate(valutazione.created_at)}
            {valutazione.valutato_da && ` · Valutata da ${valutazione.valutato_da} il ${formatDate(valutazione.valutato_il)}`}
          </p>
        </div>
        {!readonly && (
          <SimpleButton
            text="Completa valutazione"
            icon={<BsCheckLg />}
            customFunc={() => onCompleta(valutazione)}
          />
        )}
      </div>

      {/* Relazione istruttoria */}
      {valutazione.relazione_istruttoria && (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs font-semibold text-gray-600 mb-1">Relazione istruttoria</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{valutazione.relazione_istruttoria}</p>
        </div>
      )}

      {/* Comunicazione inviata */}
      {valutazione.comunicazione_inviata && valutazione.testo_comunicazione && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
            <BsCheckCircle /> Comunicazione formale inviata il {formatDate(valutazione.comunicazione_inviata_il)}
          </p>
          <p className="text-sm text-green-800 whitespace-pre-wrap">{valutazione.testo_comunicazione}</p>
        </div>
      )}

      {/* Checklist */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-gray-700 flex items-center gap-1">
            <BsClipboardCheck /> Checklist di verifica
            <span className="ml-1 text-xs font-normal text-gray-400">
              ({valutazione.checklist?.length ?? 0} voci)
            </span>
          </p>
          {!readonly && (
            <SimpleButton
              text="Aggiungi voce"
              icon={<BsPlus />}
              customFunc={() => onAggiornaChecklist(valutazione, null)}
            />
          )}
        </div>
        {(!valutazione.checklist || valutazione.checklist.length === 0) ? (
          <p className="text-xs text-gray-400 py-3 text-center border border-dashed border-gray-200 rounded">
            Nessuna voce di verifica aggiunta.
          </p>
        ) : (
          <div className="border border-gray-200 rounded-lg px-3 divide-y divide-gray-100">
            {valutazione.checklist.map((voce) => (
              <VoceChecklistRow
                key={voce.chiave}
                voce={voce}
                readonly={readonly}
                onEdit={(v) => onAggiornaChecklist(valutazione, v)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const DEFAULT_CHECKLIST_FORM = { chiave: "", etichetta: "", esito: "non_verificato", note: "" };
const DEFAULT_COMPLETA_FORM  = { esito: "completo", relazione_istruttoria: "", testo_comunicazione: "" };

export default function Fase11Tab({ procedimentoId, propostaId }) {
  const { addToast } = useStateContext();
  const api = usePppApi();
  const { can } = useAuthUser();

  const [valutazioni, setValutazioni]       = useState([]);
  const [integrazioni, setIntegrazioni]     = useState([]);
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);

  // Dialog nuova valutazione
  const [openNuovaValutazione, setOpenNuovaValutazione] = useState(false);
  const [selectedRichiestaId, setSelectedRichiestaId]   = useState("");

  // Dialog checklist voce
  const [openChecklist, setOpenChecklist]   = useState(null); // { valutazione, voce|null }
  const [checklistForm, setChecklistForm]   = useState(DEFAULT_CHECKLIST_FORM);
  const [checklistErrors, setChecklistErrors] = useState(null);

  // Dialog completa valutazione
  const [openCompleta, setOpenCompleta]     = useState(null);
  const [completaForm, setCompletaForm]     = useState(DEFAULT_COMPLETA_FORM);
  const [completaErrors, setCompletaErrors] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    let done = 0;
    const check = () => { if (++done === 2) setLoading(false); };

    api.listValutazioniIntegrazioni(procedimentoId, propostaId, {
      onSuccess: (r) => {
        const list = Array.isArray(r) ? r : (r?.data ?? []);
        setValutazioni(list);
        check();
      },
      onError: () => check(),
    });
    api.listIntegrazioni(procedimentoId, propostaId, {
      onSuccess: (r) => {
        const list = Array.isArray(r) ? r : (r?.data ?? []);
        setIntegrazioni(list.filter((x) => x.stato === "integrazione_ricevuta"));
        check();
      },
      onError: () => check(),
    });
  }, [procedimentoId, propostaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── Crea nuova valutazione ──────────────────────────────────────────────

  const handleCreaValutazione = () => {
    if (!selectedRichiestaId) return;
    setSaving(true);
    api.creaValutazioneIntegrazione(procedimentoId, propostaId, selectedRichiestaId, {
      onSuccess: () => {
        setSaving(false);
        setOpenNuovaValutazione(false);
        setSelectedRichiestaId("");
        addToast({ type: "success", message: "Valutazione avviata." });
        load();
      },
      onError: () => {
        setSaving(false);
        addToast({ type: "error", message: "Errore durante la creazione della valutazione." });
      },
    });
  };

  // ── Aggiorna voce checklist ─────────────────────────────────────────────

  const openChecklistDialog = (valutazione, voce) => {
    setChecklistErrors(null);
    setChecklistForm(voce
      ? { chiave: voce.chiave, etichetta: voce.etichetta, esito: voce.esito, note: voce.note ?? "" }
      : DEFAULT_CHECKLIST_FORM
    );
    setOpenChecklist({ valutazione, voce });
  };

  const handleSalvaVoce = () => {
    setChecklistErrors(null);
    const errs = [];
    if (!checklistForm.chiave.trim()) errs.push("La chiave identificativa è obbligatoria.");
    if (!checklistForm.etichetta.trim()) errs.push("L'etichetta è obbligatoria.");
    if (errs.length) { setChecklistErrors(errs); return; }

    setSaving(true);
    api.aggiornaChecklistVoce(procedimentoId, propostaId, openChecklist.valutazione.id, {
      chiave:    checklistForm.chiave.trim(),
      etichetta: checklistForm.etichetta.trim(),
      esito:     checklistForm.esito,
      note:      checklistForm.note.trim() || undefined,
    }, {
      onSuccess: () => {
        setSaving(false);
        setOpenChecklist(null);
        addToast({ type: "success", message: "Voce checklist aggiornata." });
        load();
      },
      onError: () => {
        setSaving(false);
        setChecklistErrors(["Errore durante il salvataggio."]);
      },
    });
  };

  // ── Completa valutazione ────────────────────────────────────────────────

  const handleCompleta = () => {
    setCompletaErrors(null);
    const errs = [];
    if (!completaForm.relazione_istruttoria.trim() || completaForm.relazione_istruttoria.trim().length < 10) {
      errs.push("La relazione istruttoria deve avere almeno 10 caratteri.");
    }
    if (errs.length) { setCompletaErrors(errs); return; }

    setSaving(true);
    api.completaValutazioneIntegrazione(procedimentoId, propostaId, openCompleta.id, {
      esito:                 completaForm.esito,
      relazione_istruttoria: completaForm.relazione_istruttoria.trim(),
      testo_comunicazione:   completaForm.testo_comunicazione.trim() || undefined,
    }, {
      onSuccess: () => {
        setSaving(false);
        setOpenCompleta(null);
        setCompletaForm(DEFAULT_COMPLETA_FORM);
        addToast({ type: "success", message: "Valutazione completata. Fase 12 avviata." });
        load();
      },
      onError: () => {
        setSaving(false);
        setCompletaErrors(["Errore durante il salvataggio."]);
      },
    });
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="space-y-6">
      {/* Header sezione */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Valutazione Integrazioni Documentali
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Art. 193, co. 6 D.lgs 36/2023 — Fase 11</p>
        </div>
        {can("ppp.valutazione_integrazioni.create") && (
          <SimpleButton
            text="Avvia nuova valutazione"
            icon={<BsPlus />}
            customFunc={() => setOpenNuovaValutazione(true)}
          />
        )}
      </div>

      {/* Banner informativo se ci sono richieste ricevute da valutare */}
      {integrazioni.length > 0 && valutazioni.filter((v) => !v.completata).length === 0 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            Ci sono <strong>{integrazioni.length}</strong> integrazione/i documentale/i ricevuta/e da valutare.
            Avvia una valutazione per procedere.
          </p>
        </div>
      )}

      {/* Lista valutazioni */}
      {valutazioni.length === 0 ? (
        <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-lg">
          <BsClipboardCheck className="mx-auto text-3xl mb-3 text-gray-300" />
          <p>Nessuna valutazione avviata.</p>
          <p className="text-xs mt-1">
            Avvia una valutazione per verificare la completezza delle integrazioni documentali ricevute.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {valutazioni.map((v) => (
            <ValutazioneCard
              key={v.id}
              valutazione={v}
              canGestisci={can("ppp.valutazione_integrazioni.manage")}
              onAggiornaChecklist={openChecklistDialog}
              onCompleta={(val) => {
                setCompletaErrors(null);
                setCompletaForm(DEFAULT_COMPLETA_FORM);
                setOpenCompleta(val);
              }}
            />
          ))}
        </div>
      )}

      {/* Dialog: avvia nuova valutazione */}
      <PppDialog
        open={openNuovaValutazione}
        title="Avvia valutazione integrazioni documentali"
        size="w-[480px]"
        onClose={() => setOpenNuovaValutazione(false)}
      >
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              Seleziona la richiesta di integrazione documentale (Fase 10) per cui avviare
              la valutazione di completezza e adeguatezza dei documenti ricevuti.
            </p>
          </div>
          {integrazioni.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              Nessuna richiesta con integrazione ricevuta disponibile.
            </p>
          ) : (
            <Select
              label="Richiesta di integrazione *"
              value={selectedRichiestaId}
              onChange={(e) => setSelectedRichiestaId(e.target.value)}
              options={[
                { value: "", label: "Seleziona..." },
                ...integrazioni.map((r) => {
                  const titolo = r.descrizione
                    ? r.descrizione
                    : Array.isArray(r.tipi_integrazione) && r.tipi_integrazione.length > 0
                      ? r.tipi_integrazione.join(", ")
                      : `Richiesta #${r.id}`;
                  const termine = new Date(r.termine_risposta).toLocaleDateString("it-IT");
                  return { value: String(r.id), label: `${titolo} — Termine: ${termine}` };
                }),
              ]}
            />
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <SimpleButton text="Annulla" customFunc={() => setOpenNuovaValutazione(false)} />
            <LoadingButton
              isLoading={saving}
              customFunc={handleCreaValutazione}
              text="Avvia valutazione"
              disabled={!selectedRichiestaId}
            />
          </div>
        </div>
      </PppDialog>

      {/* Dialog: aggiorna voce checklist */}
      <PppDialog
        open={!!openChecklist}
        title={openChecklist?.voce ? "Modifica voce checklist" : "Aggiungi voce checklist"}
        size="w-[520px]"
        onClose={() => { setOpenChecklist(null); setChecklistErrors(null); }}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                label="Chiave identificativa *"
                placeholder="es. doc_pef"
                value={checklistForm.chiave}
                disabled={!!openChecklist?.voce}
                onChange={(e) => setChecklistForm((f) => ({ ...f, chiave: e.target.value }))}
              />
            </div>
            <Select
              label="Esito verifica *"
              value={checklistForm.esito}
              onChange={(e) => setChecklistForm((f) => ({ ...f, esito: e.target.value }))}
              options={ESITO_VOCE_OPTIONS}
            />
          </div>
          <Input
            label="Etichetta descrittiva *"
            placeholder="es. Piano Economico Finanziario"
            value={checklistForm.etichetta}
            onChange={(e) => setChecklistForm((f) => ({ ...f, etichetta: e.target.value }))}
          />
          <TextArea
            label="Note (facoltative)"
            rows={3}
            value={checklistForm.note}
            onChange={(e) => setChecklistForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Osservazioni specifiche su questa voce..."
          />
          {checklistErrors?.length > 0 && <NotificationsList errors={checklistErrors} />}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <SimpleButton text="Annulla" customFunc={() => { setOpenChecklist(null); setChecklistErrors(null); }} />
            <LoadingButton isLoading={saving} customFunc={handleSalvaVoce} text="Salva voce" />
          </div>
        </div>
      </PppDialog>

      {/* Dialog: completa valutazione */}
      <PppDialog
        open={!!openCompleta}
        title="Completa valutazione integrazioni"
        size="w-[600px]"
        onClose={() => { setOpenCompleta(null); setCompletaErrors(null); }}
      >
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              <strong>Attenzione:</strong> questa operazione è definitiva. L'esito determina l'avanzamento
              automatico alla <strong>Fase 12</strong> (Ammissione/Respingimento).
            </p>
          </div>
          <Select
            label="Esito della valutazione *"
            value={completaForm.esito}
            onChange={(e) => setCompletaForm((f) => ({ ...f, esito: e.target.value }))}
            options={ESITO_VALUTAZIONE_OPTIONS}
          />
          <TextArea
            label="Relazione istruttoria sull'adeguatezza *"
            rows={5}
            value={completaForm.relazione_istruttoria}
            onChange={(e) => setCompletaForm((f) => ({ ...f, relazione_istruttoria: e.target.value }))}
            placeholder="Descrivere l'esito dell'istruttoria tecnica sulle integrazioni documentali ricevute..."
          />
          {completaForm.esito === "completo" && (
            <TextArea
              label="Testo comunicazione formale all'operatore economico"
              rows={4}
              value={completaForm.testo_comunicazione}
              onChange={(e) => setCompletaForm((f) => ({ ...f, testo_comunicazione: e.target.value }))}
              placeholder="Testo della comunicazione da inviare al Promotore/Proponente..."
            />
          )}
          {completaForm.esito !== "completo" && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-800 flex items-center gap-1">
                <BsXCircle />
                Con esito <strong>"{ESITO_VALUTAZIONE_OPTIONS.find(o => o.value === completaForm.esito)?.label}"</strong> verrà
                generato automaticamente un provvedimento motivato di respingimento (→ Fase 12).
              </p>
            </div>
          )}
          {completaErrors?.length > 0 && <NotificationsList errors={completaErrors} />}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <SimpleButton text="Annulla" customFunc={() => { setOpenCompleta(null); setCompletaErrors(null); }} />
            <LoadingButton isLoading={saving} customFunc={handleCompleta} text="Conferma e completa" />
          </div>
        </div>
      </PppDialog>
    </div>
  );
}
